import React, { useMemo } from "react";
import { barY, defineChart, dot, lineY } from "@tanstack/charts";
import { decorative } from "@tanstack/charts/mark/decorative";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";

import { ChartFrame } from "./ChartFrame";

export interface DurationCurvePoint {
    x: number;
    y: number;
    name?: string;
}

function category(value: number): string {
    return String(value);
}

export function DurationCurveChart({
    curve,
    markers,
    bars,
    ariaLabel,
    yLabel = "Zero rate",
    y2Label,
    height = 360,
    style
}: {
    curve: DurationCurvePoint[];
    markers: DurationCurvePoint[];
    bars: DurationCurvePoint[];
    ariaLabel: string;
    yLabel?: string;
    y2Label?: string;
    height?: number;
    style?: React.CSSProperties;
}): React.ReactElement {
    const definition = useMemo(() => {
        const categories = Array.from(new Set([...curve, ...markers, ...bars].map((point) => category(point.x)))).sort(
            (a, b) => Number(a) - Number(b)
        );
        const curveRows = curve.map((point) => ({ category: category(point.x), value: point.y }));
        const markerRows = markers.map((point) => ({ category: category(point.x), value: point.y, name: point.name || category(point.x) }));
        const barRows = bars.map((point) => ({ category: category(point.x), value: point.y }));
        const x = { scale: scaleBand<string>().domain(categories).padding(0.2), axis: { label: "Duration (years)" } };
        const y = { scale: scaleLinear, nice: true, grid: true, axis: { label: yLabel } };
        const line =
            curveRows.length > 0
                ? decorative(lineY(curveRows, { x: "category", y: "value", stroke: "#2186c5", strokeWidth: 2 }))
                : undefined;
        const points =
            markerRows.length > 0 ? dot(markerRows, { x: "category", y: "value", key: "name", r: 5, fill: "#43a04c" }) : undefined;
        if (barRows.length > 0) {
            return defineChart({
                marks: [
                    barY(barRows, {
                        x: "category",
                        y: "value",
                        yScale: "y2",
                        fill: "#f44336",
                        fillOpacity: 0.45,
                        key: "category"
                    }),
                    ...(line ? [line] : []),
                    ...(points ? [points] : [])
                ],
                scales: {
                    x,
                    y,
                    y2: {
                        scale: scaleLinear,
                        channel: "y",
                        side: "right",
                        nice: true,
                        grid: false,
                        axis: { label: y2Label }
                    }
                },
                tooltip
            });
        }
        return defineChart({
            marks: [...(line ? [line] : []), ...(points ? [points] : [])],
            scales: { x, y },
            tooltip
        });
    }, [bars, curve, markers, y2Label, yLabel]);

    return <ChartFrame definition={definition} ariaLabel={ariaLabel} height={height} style={style} />;
}
